import type { ReactNode } from "react";
import { ArrowRight, Check, Loader2, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface AiActionCardProps {
  icon: LucideIcon;
  title: string;
  subtitle: string;
  actionLabel: string;
  onClick: () => void;
  state?: "idle" | "loading" | "done";
  doneTitle?: string;
  doneSummary?: string;
  doneActionLabel?: string;
  secondary?: ReactNode;
}

/** Shared primary call-to-action used for AI actions in onboarding. */
export const AiActionCard = ({
  icon: Icon, title, subtitle, actionLabel, onClick, state = "idle",
  doneTitle, doneSummary, doneActionLabel = "Review", secondary,
}: AiActionCardProps) => {
  const done = state === "done";
  const loading = state === "loading";
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
      <button
        type="button"
        onClick={onClick}
        disabled={loading}
        className={cn(
          "group flex w-full flex-1 items-center gap-4 rounded-2xl p-5 text-left transition-all duration-200",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-80",
          done
            ? "border-2 border-success/40 bg-success/10 hover:border-success hover:shadow-md"
            : "border border-primary/20 bg-primary/5 text-foreground hover:border-primary/40",
        )}
      >
        <span className={cn(
          "flex h-12 w-12 shrink-0 items-center justify-center rounded-xl",
          done ? "bg-success text-success-foreground" : "bg-primary/10 text-primary",
        )}>
          {done ? <Check className="h-6 w-6" /> : <Icon className="h-6 w-6" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-base font-semibold">{done ? doneTitle || title : title}</span>
          <span className={cn("block truncate text-sm", done ? "text-muted-foreground" : "text-muted-foreground")}>
            {done ? doneSummary || subtitle : subtitle}
          </span>
        </span>
        <span className={cn(
          "hidden shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-transform group-hover:translate-x-0.5 sm:inline-flex",
          done ? "border border-success/40 bg-card text-foreground" : "border border-primary/30 bg-card text-primary",
        )}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {done ? doneActionLabel : actionLabel}
          {!loading && <ArrowRight className="h-4 w-4" />}
        </span>
      </button>
      {secondary}
    </div>
  );
};
